/**
 * HTML EDGE console — data lives here, not on a 2K DynamicTexture.
 */
import { subscribeEdge, type EdgeState, type EdgeSample } from "./feed";

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  cls: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const n = document.createElement(tag);
  n.className = cls;
  if (text !== undefined) n.textContent = text;
  return n;
}

function fmtVal(v: string | number | null | undefined, fallback = "—"): string {
  if (v === null || v === undefined || v === "") return fallback;
  return String(v);
}

export function mountEdgeConsole(host: HTMLElement): {
  setNear: (near: boolean) => void;
  destroy: () => void;
} {
  const root = el("section", "edge-console");
  root.hidden = true;
  root.setAttribute("aria-label", "EDGE control");

  const head = el("header", "edge-console-head");
  const live = el("span", "edge-console-live", "LIVE");
  const title = el("h2", "edge-console-title", "EDGE CONTROL");
  const sub = el("p", "edge-console-sub", "/api/edge  ·  real measurements only");
  const clock = el("p", "edge-console-clock mono", "—");
  head.append(live, title, sub, clock);

  const grid = el("div", "edge-console-grid");
  const cards = [
    ["COLO", "—"],
    ["RTT", "—"],
    ["HTTP", "—"],
    ["TLS", "—"],
    ["ASN", "—"],
    ["LOC", "—"],
  ].map(([k]) => {
    const card = el("article", "edge-card");
    card.append(el("p", "edge-card-k mono", k), el("p", "edge-card-v", "—"));
    grid.append(card);
    return card;
  });

  const chartWrap = el("div", "edge-console-chart");
  const chartLabel = el("p", "edge-console-chart-label mono", "RTT TRACE  ·  LIVE");
  const canvas = document.createElement("canvas");
  canvas.className = "edge-console-canvas";
  canvas.width = 640;
  canvas.height = 160;
  canvas.setAttribute("aria-hidden", "true");
  const stats = el("p", "edge-console-stats mono", "waiting for first probe…");
  chartWrap.append(chartLabel, canvas, stats);

  root.append(head, grid, chartWrap);
  host.appendChild(root);

  let near = false;
  let last: EdgeState = { samples: [], history: [] };

  const paintChart = (primary: number[]) => {
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const w = canvas.width;
    const h = canvas.height;
    const dark = document.documentElement.dataset.theme !== "light";
    ctx.clearRect(0, 0, w, h);
    ctx.fillStyle = dark ? "rgba(8,12,22,0.55)" : "rgba(255,255,255,0.45)";
    ctx.fillRect(0, 0, w, h);
    ctx.strokeStyle = dark ? "rgba(110,168,255,0.18)" : "rgba(20,40,80,0.12)";
    for (let i = 0; i <= 3; i++) {
      const y = 8 + ((h - 16) * i) / 3;
      ctx.beginPath();
      ctx.moveTo(8, y);
      ctx.lineTo(w - 8, y);
      ctx.stroke();
    }
    if (!primary.length) return;
    const min = Math.min(...primary);
    const max = Math.max(...primary);
    const span = Math.max(1, max - min);
    const pts = primary.map((rtt, i) => ({
      x: primary.length === 1 ? w / 2 : 12 + (i / (primary.length - 1)) * (w - 24),
      y: 10 + (1 - (rtt - min) / span) * (h - 24),
    }));
    ctx.beginPath();
    ctx.moveTo(pts[0].x, h - 8);
    for (const p of pts) ctx.lineTo(p.x, p.y);
    ctx.lineTo(pts[pts.length - 1].x, h - 8);
    ctx.closePath();
    ctx.fillStyle = dark ? "rgba(94,234,212,0.16)" : "rgba(15,118,110,0.12)";
    ctx.fill();
    ctx.beginPath();
    pts.forEach((p, i) => (i ? ctx.lineTo(p.x, p.y) : ctx.moveTo(p.x, p.y)));
    ctx.strokeStyle = dark ? "#5eead4" : "#0f766e";
    ctx.lineWidth = 2;
    ctx.stroke();
  };

  const render = (state: EdgeState) => {
    const latest: EdgeSample | undefined = state.samples.at(-1);
    const loc = [latest?.city, latest?.country].filter(Boolean).join(" · ") || "—";
    const values = [
      fmtVal(latest?.colo),
      latest ? `${Math.round(latest.rttMs)} ms` : "—",
      latest?.http?.replace("HTTP/", "v") ?? "—",
      latest?.tls?.replace("TLSv", "v") ?? "—",
      latest?.asn != null ? String(latest.asn) : "—",
      loc,
    ];
    cards.forEach((card, i) => {
      const v = card.querySelector(".edge-card-v");
      if (v) v.textContent = values[i];
    });
    clock.textContent = latest?.t
      ? latest.t.slice(11, 19) + "Z"
      : new Date().toISOString().slice(11, 19) + "Z";
    const liveRtts = state.samples.map((s) => s.rttMs);
    const histRtts = state.history
      .map((h) => h.rtt_ms)
      .filter((n): n is number => typeof n === "number");
    const primary = liveRtts.length ? liveRtts : histRtts;
    paintChart(primary);
    if (!primary.length) {
      stats.textContent = "waiting for first probe…";
      chartLabel.textContent = "RTT TRACE  ·  SCANNING";
    } else {
      const min = Math.min(...primary);
      const max = Math.max(...primary);
      const avg = primary.reduce((a, b) => a + b, 0) / primary.length;
      stats.textContent = `n=${primary.length}   min ${Math.round(min)}   avg ${Math.round(avg)}   max ${Math.round(max)}`;
      chartLabel.textContent = liveRtts.length
        ? "RTT TRACE  ·  LIVE SESSION"
        : "RTT TRACE  ·  STORED HISTORY";
    }
  };

  const unsub = subscribeEdge((state) => {
    last = state;
    if (near) render(state);
  });
  const tick = window.setInterval(() => {
    if (!near) return;
    clock.textContent = new Date().toISOString().slice(11, 19) + "Z";
  }, 1000);

  return {
    setNear(next) {
      if (near === next) return;
      near = next;
      root.hidden = !next;
      root.classList.toggle("is-near", next);
      if (next) render(last);
    },
    destroy() {
      unsub();
      window.clearInterval(tick);
      root.remove();
    },
  };
}
